import { useState } from "react";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { WA_LINK } from "@/data/content";
import { api, track } from "@/lib/api";
import { IMAGES } from "@/data/images";

const inputCls =
  "w-full rounded-xl border border-charcoal/15 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35 focus:border-gold-dark focus:outline-none";

const Field = ({ label, optional = true, error, children, testid }) => (
  <div>
    <label className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-charcoal/55" data-testid={testid}>
      {label} {optional && <span className="font-normal normal-case text-charcoal/35">· {optional === true ? "Optional" : optional}</span>}
    </label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-600" role="alert">{error}</p>}
  </div>
);

const Select = ({ value, onChange, options, testid, placeholder }) => (
  <select data-testid={testid} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function Estimate() {
  const { t } = useLanguage();
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const skipNull = (v) => (!v || v === "Skip" ? null : v);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!data.name || data.name.trim().length < 2) errs.name = t("form.nameRequired");
    let digits = (data.phone || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    if (!digits) errs.phone = t("form.phoneRequired");
    else if (digits.length !== 10 || !/^[6-9]/.test(digits)) errs.phone = t("form.phoneInvalid");
    if (data.email && !/^\S+@\S+\.\S+$/.test(data.email)) errs.email = t("form.emailInvalid");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    track("estimate_form_submit");
    try {
      await api.post("/enquiries", {
        name: data.name.trim(),
        phone: data.phone,
        email: data.email || null,
        form_type: "estimate",
        location: skipNull(data.location),
        project_category: skipNull(data.project_category),
        plot_area: data.plot_area || null,
        built_up_area: data.built_up_area || null,
        floors: skipNull(data.floors),
        preference: skipNull(data.preference),
        timeline: skipNull(data.timeline),
        message: data.message || null,
        company: data.company || "",
      });
      setStatus("success");
      track("estimate_form_success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <PageHero eyebrow={t("nav.estimate")} title={t("estimate.title")} sub={t("estimate.sub")} image={IMAGES.plan1} />
      <section className="bg-offwhite py-20 md:py-28" data-testid="estimate-section">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          {status === "success" ? (
            <Reveal>
              <div className="rounded-3xl border border-charcoal/10 bg-white p-12 text-center" data-testid="estimate-success">
                <CheckCircle2 className="mx-auto h-14 w-14 text-gold-dark" />
                <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-charcoal/70">{t("estimate.success")}</p>
                <a href={WA_LINK()} target="_blank" rel="noreferrer" data-testid="estimate-success-whatsapp"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-charcoal transition-transform hover:-translate-y-0.5">
                  <MessageCircle className="h-4 w-4" /> {t("estimate.continueWa")}
                </a>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} noValidate data-testid="estimate-form"
                className="space-y-7 rounded-3xl border border-charcoal/10 bg-white p-8 shadow-[0_20px_60px_rgba(18,18,18,0.06)] md:p-12">
                <div className="grid gap-7 sm:grid-cols-2">
                  <Field label={t("form.location")} optional={false}>
                    <Select testid="estimate-location-select" value={data.location} onChange={(v) => set("location", v)}
                      placeholder="Select location" options={["Tumakuru", "Bengaluru", "Other"]} />
                  </Field>
                  <Field label={t("form.category")} optional={false}>
                    <Select testid="estimate-category-select" value={data.project_category} onChange={(v) => set("project_category", v)}
                      placeholder="Select category"
                      options={["Independent Home", "Villa", "Duplex", "Apartment", "Commercial", "Renovation", "Farmhouse", "Other"]} />
                  </Field>
                  <Field label={t("form.plotArea")} optional>
                    <input data-testid="estimate-plot-input" className={inputCls} placeholder="e.g. 30×40"
                      value={data.plot_area || ""} onChange={(e) => set("plot_area", e.target.value)} />
                  </Field>
                  <Field label={t("form.builtUp")} optional>
                    <input data-testid="estimate-builtup-input" className={inputCls} placeholder="e.g. 2400 sq ft"
                      value={data.built_up_area || ""} onChange={(e) => set("built_up_area", e.target.value)} />
                  </Field>
                  <Field label={t("form.floors")} optional>
                    <Select testid="estimate-floors-select" value={data.floors} onChange={(v) => set("floors", v)}
                      placeholder="Select floors"
                      options={["Ground", "G+1", "G+2", "G+3", "G+4", "G+5", "G+6", "Custom", "Skip"]} />
                  </Field>
                  <Field label={t("form.preference")} optional>
                    <Select testid="estimate-preference-select" value={data.preference} onChange={(v) => set("preference", v)}
                      placeholder="Select preference"
                      options={["Basic", "Standard", "Premium", "Custom", "Not sure — need guidance", "Skip"]} />
                  </Field>
                  <Field label={t("form.timeline")} optional>
                    <Select testid="estimate-timeline-select" value={data.timeline} onChange={(v) => set("timeline", v)}
                      placeholder="Select timeline"
                      options={["Immediately", "1–3 months", "3–6 months", "6–12 months", "Just planning", "Custom", "Skip"]} />
                  </Field>
                </div>
                <Field label={t("form.requirements")} optional>
                  <textarea data-testid="estimate-message-input" className={`${inputCls} min-h-[96px] resize-none`}
                    value={data.message || ""} onChange={(e) => set("message", e.target.value)} />
                </Field>
                <div className="grid gap-7 border-t border-charcoal/10 pt-7 sm:grid-cols-2">
                  <Field label={`${t("form.name")} *`} optional={false} error={errors.name}>
                    <input data-testid="estimate-name-input" className={inputCls} value={data.name || ""}
                      onChange={(e) => set("name", e.target.value)} aria-invalid={!!errors.name} />
                  </Field>
                  <Field label={`${t("form.phone")} *`} optional={false} error={errors.phone}>
                    <input data-testid="estimate-phone-input" className={inputCls} inputMode="tel" value={data.phone || ""}
                      onChange={(e) => set("phone", e.target.value)} aria-invalid={!!errors.phone} />
                  </Field>
                  <Field label={t("form.email")} optional error={errors.email}>
                    <input data-testid="estimate-email-input" className={inputCls} inputMode="email" value={data.email || ""}
                      onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} />
                  </Field>
                </div>
                <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
                  className="pointer-events-none absolute h-0 w-0 opacity-0"
                  value={data.company || ""} onChange={(e) => set("company", e.target.value)} />
                {status === "error" && (
                  <p className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs text-red-700" role="alert" data-testid="estimate-error">
                    {t("form.error")}
                  </p>
                )}
                <button type="submit" data-testid="estimate-submit-button" disabled={status === "submitting"}
                  className="w-full rounded-full bg-gold px-7 py-4 text-sm font-bold text-charcoal shadow-[0_8px_30px_rgba(212,175,55,0.25)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-soft disabled:opacity-60">
                  {status === "submitting" ? t("form.sending") : t("cta.submit")}
                </button>
                <p className="text-center text-xs text-charcoal/45">
                  By submitting, you agree to be contacted by D & N Constructions about your enquiry.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
